import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { EntrySearchRow } from '../types'

const PAGE_SIZE = 24

interface UseSearchResult {
  entries: EntrySearchRow[]
  total: number
  loading: boolean
  error: string | null
  page: number
  totalPages: number
  setPage: (p: number) => void
}

export function useSearch(query: string): UseSearchResult {
  const [entries, setEntries]   = useState<EntrySearchRow[]>([])
  const [total, setTotal]       = useState(0)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [page, setPageState]    = useState(0)

  const setPage = useCallback((p: number) => setPageState(p), [])

  // Reset to page 0 when query changes
  useEffect(() => {
    setPageState(0)
  }, [query])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      setLoading(true)
      setError(null)

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error: rpcError } = await (supabase.rpc as any)(
          'search_entries',
          {
            search_term: query.trim(),
            page_offset: page * PAGE_SIZE,
            page_limit:  PAGE_SIZE,
          }
        )

        if (cancelled) return
        if (rpcError) throw rpcError

        const rows = (data ?? []) as EntrySearchRow[]
        setEntries(rows)
        setTotal(rows.length > 0 ? Number(rows[0].total_count) : 0)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e))
          setEntries([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    // Debounce only on query change; page change fires immediately
    const delay = page === 0 ? 300 : 0
    const timer = setTimeout(run, delay)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [query, page])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return { entries, total, loading, error, page, totalPages, setPage }
}
